import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/router";
import { BoardblendLoader } from "@/components/boardblend/boardblend-loader";
import { showcases } from "@/showcases";
import { getApiErrorMessage } from "@/store/api";
import { useForkShowcaseMutation } from "@/store/entry-api";
import styles from "@/styles/showcase.module.css";

export function ShowcaseSection({ displayName }: { displayName?: string }) {
  const router = useRouter();
  const [forkShowcase, forkState] = useForkShowcaseMutation();

  const openShowcase = async (slug: string) => {
    forkState.reset();

    try {
      const result = await forkShowcase({ displayName, slug }).unwrap();
      await router.push(`/session/${result.sessionId}`);
    } catch {
      // RTK Query exposes the request error through forkState.
    }
  };

  return (
    <section
      className={styles.homeSection}
      id="examples"
      aria-labelledby="showcase-heading"
      aria-busy={forkState.isLoading}
    >
      {forkState.isLoading && (
        <div className={styles.loadingState}>
          <BoardblendLoader />
        </div>
      )}
      <div className={styles.sectionHeading}>
        <h2 id="showcase-heading">Explore real examples</h2>
        <span>
          See how teams use Boardblend to bring different perspectives together
          in one shared canvas, leading to deeper discussion and clearer
          decisions.
        </span>
      </div>
      <div className={styles.cardGrid}>
        {showcases.map((showcase) => (
          <button
            className={styles.showcaseCard}
            disabled={forkState.isLoading}
            key={showcase.slug}
            onClick={() => void openShowcase(showcase.slug)}
            type="button"
          >
            <div className={styles.cardPreview}>
              <Image
                alt=""
                aria-hidden="true"
                height={720}
                sizes="(max-width: 920px) 100vw, 33vw"
                src={`/showcases/${showcase.slug}.png?v=4`}
                unoptimized
                width={1040}
              />
            </div>
            <div className={styles.cardCopy}>
              <span>{showcase.category}</span>
              <h3>{showcase.title}</h3>
              <p>{showcase.description}</p>
              <strong>
                Open in canvas
                <ArrowUpRight aria-hidden="true" />
              </strong>
            </div>
          </button>
        ))}
      </div>
      {forkState.error && (
        <p className={styles.formError} role="alert">
          {getApiErrorMessage(
            forkState.error,
            "Could not open this brainstorm",
          )}
        </p>
      )}
    </section>
  );
}
